import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import {
  lookup,
  sso as ssoApi,
  login as loginApi,
  logout as logoutApi,
  fetchTokenInfo as fetchTokenInfoApi,
  forgotPassword as forgotPasswordApi,
  recoverPassword as recoverPasswordApi,
  changePassword as changePasswordApi,
  verifyPassword as verifyPasswordApi,
  availability as availabilityApi,
  disconnectCalendar as disconnectCalendarApi,
  fetchUsersApi,
  fetchUsersEx,
  getBackgroundEvents,
  addUserApi,
  updateUserApi,
  updateProfileApi,
  updateBookingSettings as updateBookingSettingsApi,
  updateAvailibility as updateAvailibilityApi,
  sessionInfo as sessionInfoApi,
  user as userApi,
  addLicense as addLicenseApi,
  removeLicense as removeLicenseApi,
  addICIMSUserId,
  removeICIMSUserId,
  getAtsUser,
  ingestAtsUser,
  expirePassword as expirePasswordApi,
  addUserInbox as addUserInboxApi,
  removeUserInbox as removeUserInboxApi,
  updateUserDisableAPI,
  updateUserDeleteAPI,
  updateRoleAPI,
  fetchResetPasswordApi,
  sendAsUser,
} from 'util/Api';
import { getCustomerTemplates } from 'util/Api/customer';
import { saveMessageTemplate, removeIntegrationMessageTemplate } from 'util/Api/campaign';

export const setAuthMethod = createAction('user/setAuthMethod');
export const setForgotPasswordState = createAction('user/setForgotPasswordState');
export const setUsername = createAction('user/setUsername');
export const setPassword = createAction('user/setPassword');
export const setShowScreen = createAction('user/setShowScreen');
export const setShowInvalidLink = createAction('user/setShowInvalidLink');
export const setMessageOptionsFormsUpdated = createAction('user/setMessageOptionsFormsUpdated');
export const setIsInternal = createAction('user/setIsInternal');
export const resetState = createAction('resetState');
export const setAPIError = createAction('setAPIError');

export const handleSnackClose = createAction('userDeleted');

export const resetUserSave = createAction('user/resetUserSave');

export const passwordAndScreenUpdates = createAction('user/passwordAndScreenUpdates');

export const disconnectFacebookUpdate = createAction('user/disconnectFacebookUpdate');

export const resetSendAsUser = createAction('user/resetSendAsUser');

export const lookupRegion = createAsyncThunk(
  'user/lookup',
  async ({ username, _enableEuRegion = false }, { rejectWithValue }) => {
    try {
      const response = await lookup({ username });

      return { ...response, _enableEuRegion };
    } catch (err) {
      return rejectWithValue({ message: 'error' });
    }
  },
);

export const sso = createAsyncThunk('user/sso', async ({ username }, { rejectWithValue }) => {
  try {
    return await ssoApi({ username });
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const forgotPassword = createAsyncThunk(
  'user/forgotPassword',
  async ({ username }, { rejectWithValue }) => {
    try {
      return await forgotPasswordApi({ username });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const verifyPassword = createAsyncThunk(
  'user/verifyPassword',
  async ({ id }, { rejectWithValue }) => {
    try {
      return await verifyPasswordApi({ id });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const availability = createAsyncThunk(
  'user/availability',
  async (_args, { rejectWithValue }) => {
    try {
      return await availabilityApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const disconnectCalendar = createAsyncThunk(
  'user/disconnectCalendar',
  async ({ type }, { rejectWithValue }) => {
    try {
      return await disconnectCalendarApi({ type });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateBookingSettings = createAsyncThunk(
  'user/updateBookingSettings',
  async ({ bookingDelay, bookingDuration, delayBookingToNextDay }, { rejectWithValue }) => {
    try {
      return await updateBookingSettingsApi({
        bookingDelay,
        bookingDuration,
        delayBookingToNextDay,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateAvailibility = createAsyncThunk(
  'user/updateAvailibility',
  async ({ calendarAvailability }, { rejectWithValue }) => {
    try {
      return await updateAvailibilityApi({
        calendarAvailability,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const recoverPassword = createAsyncThunk(
  'user/recoverPassword',
  async ({ id, password }, { rejectWithValue }) => {
    try {
      return await recoverPasswordApi({ id, password });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const changePassword = createAsyncThunk(
  'user/changePassword',
  async ({ username, password, newPassword }, { rejectWithValue }) => {
    try {
      return await changePasswordApi({ username, password, newPassword });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchTokenInfo = createAsyncThunk(
  'user/fetchTokenInfo',
  async (_args, { rejectWithValue }) => {
    try {
      return await fetchTokenInfoApi();
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const sessionInfo = createAsyncThunk(
  'user/sessionInfo',
  async (noRefresh, { rejectWithValue, dispatch }) => {
    try {
      const r = await sessionInfoApi();
      if (!noRefresh) {
        await dispatch(fetchTokenInfo());
      }
      return r;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const login = createAsyncThunk(
  'user/login',
  async ({ username, password, isRememberMe }, { rejectWithValue, dispatch }) => {
    try {
      const r = await loginApi({ username, password, isRememberMe });
      await dispatch(sessionInfo(true));
      return r;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const resetLoginError = createAction('resetLoginError');

export const logout = createAsyncThunk('user/logout', async (_args, { rejectWithValue }) => {
  try {
    return await logoutApi();
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const usersListInfo = createAsyncThunk(
  'manager/users',
  async ({ page, itemsPerPage, sort, filter, phone }, { rejectWithValue }) => {
    try {
      return await fetchUsersApi({ page, itemsPerPage, sort, filter, phone });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchUsersExInfo = createAsyncThunk(
  'manager/users',
  async ({ filters }, { rejectWithValue }) => {
    try {
      return await fetchUsersEx({ filters });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchBackgroundEvents = createAsyncThunk(
  'user/fetchBackgroundEvents',
  async ({ pageNumber, itemsPerPage, filterValue }, { rejectWithValue }) => {
    try {
      return await getBackgroundEvents({ pageNumber, itemsPerPage, filterValue });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchAddUser = createAsyncThunk(
  'user/fetchAddUser',
  async (
    { firstName, lastName, username, emailAddress, desiredCode, role, isChecked },
    { rejectWithValue },
  ) => {
    try {
      return await addUserApi({
        firstName,
        lastName,
        username,
        emailAddress,
        desiredCode,
        role,
        isChecked,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchUpdateUser = createAsyncThunk(
  'user/fetchUpdateUser',
  async ({ firstName, lastName, id }, { rejectWithValue }) => {
    try {
      return await updateUserApi({
        firstName,
        lastName,
        id,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateProfileSettings = createAsyncThunk(
  'user/updateProfileSettings',
  async (
    {
      fname,
      lname,
      timezone,
      contactPhoneNumber,
      automaticTextMsg,
      callForwardingNumber,
      isCallForwarding,
      isDisableVoicemail,
      isDuplicateMessageBlocking,
      voicemailMessage,
      unreadMessageNotifications,
      optedOutMessage,
    },
    { rejectWithValue },
  ) => {
    try {
      return await updateProfileApi({
        fname,
        lname,
        timezone,
        contactPhoneNumber,
        automaticTextMsg,
        callForwardingNumber,
        isCallForwarding,
        isDisableVoicemail,
        isDuplicateMessageBlocking,
        voicemailMessage,
        unreadMessageNotifications,
        optedOutMessage,
        /* TODO: This is true in all the scenarios; To be removed after confirming if this doesnt have any significance */
        sendInitialOptOut: true,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchUserDetails = createAsyncThunk(
  'user/details',
  async ({ userId }, { rejectWithValue }) => {
    try {
      return await userApi(userId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchSendAsUserDetails = createAsyncThunk(
  'user/sendAsUser',
  async ({ userName }, { rejectWithValue }) => {
    try {
      return await sendAsUser(userName);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const expirePassword = createAsyncThunk(
  'user/expirePassword',
  async ({ userId }, { rejectWithValue }) => {
    try {
      return await expirePasswordApi({ userId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const addUserInbox = createAsyncThunk(
  'user/addUserInbox',
  async ({ userId, userName, inboxName }, { rejectWithValue }) => {
    try {
      return await addUserInboxApi({ userId, userName, inboxName });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const removeUserInbox = createAsyncThunk(
  'user/removeUserInbox',
  async ({ userId, userName }, { rejectWithValue }) => {
    try {
      return await removeUserInboxApi({ userId, userName });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateDisableUser = createAsyncThunk(
  'user/disable',
  async ({ userId, isDisable }, { rejectWithValue }) => {
    try {
      return await updateUserDisableAPI({ userId, isDisable });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const addLicense = createAsyncThunk(
  'user/addLicense',
  async ({ userId, areaCode }, { rejectWithValue }) => {
    try {
      return await addLicenseApi({ userId, areaCode });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchDeleteUser = createAsyncThunk(
  'user/delete',
  async ({ userId, role, firstName, lastName }, { rejectWithValue }) => {
    try {
      return await updateUserDeleteAPI({ userId, role, firstName, lastName });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const removeLicense = createAsyncThunk(
  'user/removeLicense',
  async ({ userId }, { rejectWithValue }) => {
    try {
      return await removeLicenseApi({ userId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchUserDetailsByRole = createAsyncThunk(
  'user/details',
  async ({ userId, firstName, lastName, role }, { rejectWithValue }) => {
    try {
      return await updateRoleAPI({ userId, firstName, lastName, role });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const getTemplateData =
  (isUserResponseTemplate) =>
  async (
    {
      pageNumber,
      itemsPerPage,
      sort,
      code,
      shareFilter = isUserResponseTemplate ? 'USERONLY' : 'ALL',
      type,
    },
    { rejectWithValue },
  ) => {
    try {
      return await getCustomerTemplates({
        pageNumber,
        itemsPerPage,
        sort,
        code,
        shareFilter,
        type,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  };

export const fetchUserResponseTemplates = createAsyncThunk(
  'customer/fetchUserResponseTemplates',
  getTemplateData('isUserResponseTemplate'),
);

export const fetchMessageTemplate = createAsyncThunk(
  'user/fetchMessageTemplate',
  getTemplateData(),
);

export const fetchResetPassword = createAsyncThunk(
  'user/fetchResetPassword',
  async ({ userName }, { rejectWithValue }) => {
    try {
      return await fetchResetPasswordApi({ userName });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const saveMessageTemplateInbound = createAsyncThunk(
  'user/saveMessageTemplateInbound',
  async ({ code, name, text, templateId, share, type }, { rejectWithValue }) => {
    try {
      return await saveMessageTemplate({
        code,
        name,
        text,
        templateId,
        shareTemplate: share,
        type,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
export const deleteMessageTemplate = createAsyncThunk(
  'user/deleteMessageTemplate',
  async (templateId, { rejectWithValue }) => {
    try {
      return await removeIntegrationMessageTemplate(templateId);
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const addAtsPersonId = createAsyncThunk(
  'user/addAtsPersonId',
  async ({ userId, icimsUserId }, { rejectWithValue }) => {
    try {
      return await addICIMSUserId({ userId, icimsUserId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const removeAtsPersonId = createAsyncThunk(
  'user/removeAtsPersonId',
  async ({ userId, icimsUserId }, { rejectWithValue }) => {
    try {
      return await removeICIMSUserId({ userId, icimsUserId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchAtsUser = createAsyncThunk(
  'user/fetchAtsUser',
  async ({ atsUserId, email }, { rejectWithValue }) => {
    try {
      return await getAtsUser({ atsUserId, email });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const importAtsUser = createAsyncThunk(
  'user/importAtsUser',
  async ({ atsUserId, email }, { rejectWithValue }) => {
    try {
      return await ingestAtsUser({ atsUserId, email });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const setMessageTemplateIsEdittrue = createAction('thread/setMessageTemplateIsEdittrue');
export const resetMessageTemplateSaveStatus = createAction('thread/resetTemplateSaveStatus');

export const resetProfileUpdateStatus = createAction('thread/resetProfileUpdateStatus');
export const resetAtsUser = createAction('user/resetAtsUser');

export const setIsRowsPerPageChanged = createAction('user/setIsRowsPerPageChanged');
